import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle2, 
  ArrowRight, 
  Target, 
  Briefcase, 
  Layers, 
  Clock, 
  Unlock, 
  Sparkles,
  Link2
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { normalizeAcronyms } from '../utils/normalizeAcronyms';
import { cachedFetch } from '../utils/fetchWithCache';

interface PathGuidancePanelProps {
  companyId: string;
  accessToken: string;
  className?: string;
  onActionClick?: (item: any) => void;
  /** Shows "View Full Plan"; the parent decides where the full plan lives. Omitted = no button. */
  onViewPlan?: () => void;
  /** Pre-loaded data from parent bundle -- skips HTTP fetch when provided */
  initialData?: { recommendations: any; context: any } | null;
}

/**
 * Parses an unlocksText string like "Required before configuring SRM SLA and Incident SLA"
 * into an array of dependency names for badge rendering.
 */
function parseDependencyNames(unlocksText: string): string[] {
  if (!unlocksText) return [];
  // Strip the "Required before configuring " prefix
  const stripped = unlocksText
    .replace(/^Required before configuring\s*/i, '')
    .replace(/^Unlocks\s*/i, '');
  if (!stripped || stripped === unlocksText) return []; // No prefix matched — it's a generic message
  return stripped.split(/\s+and\s+/i).map(s => s.trim()).filter(Boolean);
}

export function PathGuidancePanel({ companyId, accessToken, className, onActionClick, onViewPlan, initialData }: PathGuidancePanelProps) {
  const [data, setData] = useState<any>(initialData || null);
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    // Skip fetch if parent already provided the data
    if (initialData) {
      setData(initialData);
      setLoading(false);
      return;
    }
    if (companyId) {
      loadRecommendations();
    }
  }, [companyId, initialData]);

  const loadRecommendations = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const response = await cachedFetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/client/recommendations/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="h-64 bg-gray-50 animate-pulse rounded-lg border border-gray-200" />;
  if (!data) return null;

  const { recommendations, context } = data;
  const { primary, upcoming } = recommendations || {};
  const { hlos, consultantParallelWork, completedMilestones } = context || {};

  // Parse dependency names from unlocksText for badge rendering
  const primaryDeps = parseDependencyNames(primary?.unlocksText || '');

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 1. HLO CONTEXT BAR */}
      <div className="flex items-center gap-3 text-sm text-gray-500 bg-white px-4 py-2 rounded-full border shadow-sm w-fit mx-auto mb-6">
        <Target className="size-4 text-indigo-600" />
        <span className="font-medium">Optimization Goals:</span>
        <div className="flex gap-2">
          {hlos?.map((hlo: string) => (
            <Badge key={hlo} variant="secondary" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-[10px] uppercase tracking-wide">
              {normalizeAcronyms(hlo)}
            </Badge>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 2. HERO: NEXT RECOMMENDED STEP */}
        <div className="lg:col-span-2">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 ease-out">
            <Card className="border-l-4 border-l-indigo-600 shadow-md bg-gradient-to-br from-white to-indigo-50/30 overflow-hidden relative">
               {/* Background Pattern */}
              <div className="absolute top-0 right-0 p-3 opacity-5">
                <Sparkles className="size-48 text-indigo-900" />
              </div>

              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <Badge className="bg-indigo-600 hover:bg-indigo-700 mb-2">Next Recommended Step</Badge>
                    {/* TITLE: Clean, normalized */}
                    <CardTitle className="text-2xl text-indigo-900">
                      {normalizeAcronyms(primary?.name || "Review Configuration")}
                    </CardTitle>
                    {/* SUBTITLE: Outcome-driven, no repetition */}
                    <CardDescription className="text-base mt-1">
                      {normalizeAcronyms(primary?.explanation || "Complete this step to unlock the next phase of your implementation.")}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {/* DEPENDENCY INDICATOR: Badges instead of prose */}
                <div className="bg-white/60 rounded-lg p-4 border border-indigo-100 mb-6 backdrop-blur-sm">
                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-100 p-2 rounded-full text-indigo-700 shrink-0 mt-0.5">
                      {primaryDeps.length > 0 ? (
                        <Link2 className="size-4" />
                      ) : (
                        <Unlock className="size-4" />
                      )}
                    </div>
                    <div>
                      {primaryDeps.length > 0 ? (
                        <>
                          <h4 className="font-semibold text-sm text-indigo-900 mb-2">Required before:</h4>
                          <div className="flex flex-wrap gap-2">
                            {primaryDeps.map((dep) => (
                              <Badge 
                                key={dep} 
                                variant="outline"
                                className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs px-2.5 py-1"
                              >
                                {normalizeAcronyms(dep)}
                              </Badge>
                            ))}
                          </div>
                        </>
                      ) : (
                        <>
                          <h4 className="font-semibold text-sm text-indigo-900 mb-1">Why this step?</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {normalizeAcronyms(
                              primary?.unlocksText || 
                              "This foundational item is required before we can proceed to more advanced configurations."
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5"
                    onClick={() => onActionClick && onActionClick(primary)}
                  >
                    Start This Step <ArrowRight className="ml-2 size-4" />
                  </Button>
                  {onViewPlan && (
                    <Button variant="outline" className="w-full sm:w-auto border-gray-300 text-gray-700" onClick={onViewPlan}>
                      View Full Plan
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 4. PREVIOUSLY COMPLETED */}
          {completedMilestones && completedMilestones.length > 0 && (
             <div className="mt-6 pl-2">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Already Completed</h4>
                <div className="flex flex-wrap gap-2">
                    {completedMilestones.map((item: any) => (
                        <Badge key={item.id} variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1.5 py-1">
                            <CheckCircle2 className="size-3" /> {normalizeAcronyms(item.name)}
                        </Badge>
                    ))}
                    {completedMilestones.length >= 5 && (
                        <Badge variant="outline" className="text-gray-400 border-dashed border-gray-300">
                            + more
                        </Badge>
                    )}
                </div>
             </div>
          )}
        </div>

        {/* 3. SIDEBAR: UPCOMING & PARALLEL */}
        <div className="space-y-6">
            {/* UPCOMING */}
            <Card className="shadow-sm border-gray-200">
                <CardHeader className="pb-3 pt-4">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Layers className="size-4 text-gray-500" />
                        Coming Up Next
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {upcoming && upcoming.length > 0 ? (
                        upcoming.map((item: any, i: number) => {
                            const itemDeps = parseDependencyNames(item.unlocksText || '');
                            return (
                                <div key={item.id} className="flex gap-3 items-start p-2 rounded-md hover:bg-gray-50 transition-colors">
                                    <div className="bg-gray-100 text-gray-500 font-bold text-xs h-6 w-6 flex items-center justify-center rounded-full shrink-0">
                                        {i + 1}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-gray-800">
                                            {normalizeAcronyms(item.name)}
                                        </div>
                                        {itemDeps.length > 0 ? (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {itemDeps.map((dep: string) => (
                                                    <Badge key={dep} variant="outline" className="text-[10px] px-1.5 py-0 text-gray-500 border-gray-200">
                                                        {normalizeAcronyms(dep)}
                                                    </Badge>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {normalizeAcronyms(item.unlocksText || "Required step")}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-sm text-gray-400 italic">No pending items.</div>
                    )}
                </CardContent>
            </Card>

            {/* PARALLEL WORK */}
            <Card className="shadow-sm border-gray-200 bg-slate-50">
                <CardHeader className="pb-3 pt-4">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Briefcase className="size-4 text-slate-500" />
                        Consultant Parallel Work
                    </CardTitle>
                    <CardDescription className="text-xs">
                        Happening in the background
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    {consultantParallelWork && consultantParallelWork.length > 0 ? (
                        consultantParallelWork.map((item: any) => (
                            <div key={item.id} className="flex gap-2 items-center text-sm text-slate-700">
                                <div className="h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                <span className="truncate">{normalizeAcronyms(item.name)}</span>
                            </div>
                        ))
                    ) : (
                         <div className="text-xs text-slate-400">Your consultant is reviewing your latest inputs.</div>
                    )}
                </CardContent>
            </Card>
        </div>
      </div>
    </div>
  );
}